import { useQuery } from '@tanstack/react-query';

import { paymentService } from '@src/api/services';
import {
  IMolliePaymentRequest,
  IPreparePayData,
  IUsePreparePayData,
  IUseStripePaymentIntent,
  IUseTransactionStatus,
} from '@src/api/types/payment.types';
import {
  DEFAULT_CURRENCY,
  GATEWAYS,
  MOLLIE_PAYMENT_METHOD_CODES,
  MOLLIE_PAYMENT_METHODS_ORDER,
  PAYMENT_INTENT_STATUSES,
  QUERY_KEYS,
  TRACKING_DOMAINS,
} from '@src/constants';
import { fetchWithInterval } from '@src/helpers/axios.helpers';
import { getCountryCodeBySubdomain } from '@src/helpers/host.helpers';
import {
  checkMolliePaymentMethods,
  createUUID,
  getAfterPaymentSearchQueries,
  isEventPushed,
} from '@src/helpers/payment.helpers';
import { timeout } from '@src/helpers/timeout.helpers';
import { IMolliePaymentMethod } from '@src/interfaces/payments.interfaces';
import { IGateway } from '@src/interfaces/subscriptions.interfaces';

import { settingsService } from '../services/settings.services';
import { useUserInfo } from './user.queries';

export const usePreparePayData = ({ searchParams }: IUsePreparePayData) => {
  return useQuery<IPreparePayData>({
    queryKey: [QUERY_KEYS.preparePayData, { ...searchParams }],
    queryFn: async () => {
      const { isPaymentFailed, planID } =
        getAfterPaymentSearchQueries(searchParams);

      const countryCode = getCountryCodeBySubdomain();

      const domainResponse = await settingsService.webDomain({
        domain: countryCode,
      });

      const currencyResponse = await paymentService.currency({
        domain: countryCode,
      });

      const gateway = domainResponse.data.gateway;

      const isMolliePayment = gateway === GATEWAYS.MOLLIE;
      const isStripePayment = gateway === GATEWAYS.STRIPE;
      const isPrimerPayment = gateway === GATEWAYS.PRIMER;

      const primerFroudNetID = isPrimerPayment ? createUUID() : undefined;

      return {
        ...currencyResponse.data.data,
        ...domainResponse.data,
        planID,
        isPaymentFailed,
        isMolliePayment,
        isStripePayment,
        isPrimerPayment,
        primerFroudNetID,
      };
    },
    structuralSharing: false,
    placeholderData: undefined,
    networkMode: 'online',
    retry: false,
    retryOnMount: false,
    initialData: undefined,
    enabled: true,
  });
};

export const useTransactionStatus = ({
  payment_intent,
  planID,
  onRegistrationCompleted,
  enabled,
}: IUseTransactionStatus) => {
  const { data: user } = useUserInfo({ enabled });

  return useQuery({
    enabled: !!enabled && !!user,
    queryKey: [QUERY_KEYS.checkPaymentStatus],
    queryFn: async () => {
      if (!payment_intent || !planID) {
        if (user?.is_registration_completed) return true;
        throw Error('Payment Intent and plan ID is required');
      }

      let orderID: string | undefined = undefined;
      let orderPrice: number | undefined = undefined;
      let orderCurrency = DEFAULT_CURRENCY;

      const response = await fetchWithInterval<boolean>({
        seconds: 60,
        interval: 3,
        action: async (signal) => {
          const res = await paymentService.intent({
            reference: payment_intent!,
            signal,
          });

          const {
            id,
            currency = orderCurrency,
            price,
            status,
          } = res.data || {};

          orderID = id;
          orderCurrency = currency;
          orderPrice = price;

          if (status === PAYMENT_INTENT_STATUSES.SUCCESS) return true;
          if (status === PAYMENT_INTENT_STATUSES.CANCELLED) return false;

          return undefined;
        },
      });

      if (!response) throw Error('Payment status is failed');

      if (orderCurrency.toUpperCase() !== DEFAULT_CURRENCY) {
        const exchangeResponse = await paymentService.exchangeToBase({
          value: (orderPrice || 0).toString(),
          currency: orderCurrency,
        });
        orderPrice = exchangeResponse.data.price.value;
        orderCurrency = DEFAULT_CURRENCY;
      }

      const dataLayerEvent = {
        event: 'purchase',
        currency: orderCurrency,
        value: orderPrice,
        orderID,
        email: user?.email,
        planID,
      };

      if (!isEventPushed(dataLayerEvent.event)) {
        console.log('data: ', dataLayerEvent);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(dataLayerEvent);
      }

      TRACKING_DOMAINS.forEach((domain) => {
        const img = document.createElement('img');
        img.src = `https://${domain}/adclick.php?revenue=${orderPrice}&custom1=${orderID}`;
        img.width = 1;
        img.height = 1;
        img.style.display = 'none';
        document.body.append(img);
      });

      await timeout(1000);

      if (user?.is_registration_completed) onRegistrationCompleted?.();

      return response;
    },
    retry: false,
  });
};

export const useStripePaymentIntent = ({
  stripe,
  clientSecret,
}: IUseStripePaymentIntent) => {
  return useQuery({
    queryKey: [QUERY_KEYS.stripePaymentIntent, { clientSecret, stripe }],
    queryFn: async () => {
      const { paymentIntent } = await stripe!.retrievePaymentIntent(
        clientSecret!,
      );

      return !!paymentIntent;
    },
    enabled: !!stripe && !!clientSecret,
    structuralSharing: false,
    placeholderData: undefined,
    networkMode: 'online',
  });
};

export interface IUseMolliePaymentMethods extends Pick<
  IMolliePaymentRequest,
  'amount' | 'currency'
> {
  gateway?: IGateway;
}

export const useMolliePaymentMethods = ({
  gateway,
  amount,
  currency,
}: IUseMolliePaymentMethods) => {
  const isMolliePayment = gateway === GATEWAYS.MOLLIE;

  return useQuery<IMolliePaymentMethod[]>({
    queryKey: [QUERY_KEYS.preparePayData, { gateway, amount, currency }],
    queryFn: async () => {
      const countryCode = getCountryCodeBySubdomain();

      const paymentMethodsResponse = await paymentService.molliePaymentMethods({
        domain: countryCode,
        amount,
        currency: currency || '',
      });

      let paymentMethods =
        paymentMethodsResponse.data.data.sort(
          (a, b) =>
            MOLLIE_PAYMENT_METHODS_ORDER.indexOf(a.code) -
            MOLLIE_PAYMENT_METHODS_ORDER.indexOf(b.code),
        ) || [];

      if (!window.ApplePaySession) {
        paymentMethods = paymentMethods?.filter(
          ({ code }) => code !== MOLLIE_PAYMENT_METHOD_CODES.APPLE_PAY,
        );
      }

      return paymentMethods.map((method) => ({
        ...method,
        ...checkMolliePaymentMethods(method),
      }));
    },
    structuralSharing: false,
    placeholderData: undefined,
    networkMode: 'online',
    retry: false,
    retryOnMount: false,
    initialData: undefined,
    enabled: isMolliePayment && gateway && !!amount && !!currency,
  });
};
